import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'react-toastify';
import { Helmet } from 'react-helmet-async';
import Footer from '../components/Footer';
import api from '../utils/api';

const Login = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotPasswordStep, setForgotPasswordStep] = useState(1); // 1: Email, 2: OTP & New Password
  const [forgotPasswordData, setForgotPasswordData] = useState({
    email: '',
    otp: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [forgotPasswordLoading, setForgotPasswordLoading] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  // Password validation function
  const validatePassword = (password) => {
    if (!password) {
      return { valid: false, message: 'Password is required' };
    }

    if (password.length < 6) {
      return { valid: false, message: 'Password must be at least 6 characters long' };
    }

    if (!/[a-z]/.test(password)) {
      return { valid: false, message: 'Password must contain at least one lowercase letter' };
    }

    if (!/[A-Z]/.test(password)) {
      return { valid: false, message: 'Password must contain at least one uppercase letter' };
    }

    if (!/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)) {
      return { valid: false, message: 'Password must contain at least one special character (!@#$%^&*()_+-=[]{}|;:,.<>?)' };
    }

    return { valid: true, message: 'Password meets all requirements' };
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleForgotPasswordChange = (e) => {
    setForgotPasswordData({
      ...forgotPasswordData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const result = await login(formData.email, formData.password);

      if (result.success) {
        toast.success('Login successful!');
        navigate('/home');
      } else {
        toast.error(result.message);
      }
    } catch (error) {
      toast.error('An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPasswordSubmit = async (e) => {
    e.preventDefault();
    setForgotPasswordLoading(true);

    try {
      if (forgotPasswordStep === 1) {
        // Send OTP to email
        const response = await api.post('/auth/forgot-password', {
          email: forgotPasswordData.email
        });

        if (response.data.success) {
          toast.success('OTP sent to your email!');
          setForgotPasswordStep(2);
        } else {
          toast.error(response.data.message || 'Failed to send OTP');
        }
      } else {
        // Verify OTP and reset password
        if (forgotPasswordData.newPassword !== forgotPasswordData.confirmPassword) {
          toast.error('Passwords do not match');
          return;
        }

        // Enhanced password validation
        const passwordValidation = validatePassword(forgotPasswordData.newPassword);
        if (!passwordValidation.valid) {
          toast.error(passwordValidation.message);
          return;
        }

        const response = await api.post('/auth/reset-password', {
          email: forgotPasswordData.email,
          otp: forgotPasswordData.otp,
          newPassword: forgotPasswordData.newPassword
        });

        if (response.data.success) {
          toast.success('Password reset successfully! You can now login.');
          setShowForgotPassword(false);
          setForgotPasswordStep(1);
          setForgotPasswordData({
            email: '',
            otp: '',
            newPassword: '',
            confirmPassword: ''
          });
        } else {
          toast.error(response.data.message || 'Failed to reset password');
        }
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'An error occurred. Please try again.');
    } finally {
      setForgotPasswordLoading(false);
    }
  };

  const resetForgotPassword = () => {
    setShowForgotPassword(false);
    setForgotPasswordStep(1);
    setForgotPasswordData({
      email: '',
      otp: '',
      newPassword: '',
      confirmPassword: ''
    });
  };

  return (
    <>
      <Helmet>
        <title>Login - VestrioZ</title>
        <meta
          name="description"
          content="Login to your VestrioZ account to rent, share, and sell fashion and jewellery."
        />
        <meta name="keywords" content="login, VestrioZ, fashion rental, account access" />
      </Helmet>
      <div className="min-h-screen" style={{ background: 'linear-gradient(135deg, #DFD0B8 0%, #f5f1e8 100%)' }}>
        {/* Navigation */}
        <nav className="backdrop-blur-md shadow-xl border-b" style={{ background: 'rgba(223, 208, 184, 0.95)', borderColor: 'rgba(148, 137, 121, 0.3)' }}>
          <div className="max-w-7xl mx-auto px-4">
            <div className="flex justify-between items-center py-2">
              <div className="flex items-center">
                <Link to="/" className="flex items-center">
                  <img
                    src="/images/VESTRIOZ.png"
                    alt="VestrioZ Logo"
                    className="h-14 sm:h-16 w-auto object-contain"
                    onError={(e) => {
                      // Fallback to icon if logo image fails to load
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'flex';
                    }}
                  />
                  <div className="h-14 sm:h-16 w-14 sm:w-16 rounded-xl flex items-center justify-center shadow-lg" style={{ backgroundColor: '#222831', display: 'none' }}>
                    <i className="fas fa-exchange-alt text-white text-xl sm:text-2xl"></i>
                  </div>
                </Link>
              </div>
            </div>
          </div>
        </nav>

        {/* Login Form */}
        <div className="min-h-screen flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
          <div className="max-w-md w-full">
            {!showForgotPassword ? (
              // Login Form
              <div className="backdrop-blur-sm rounded-2xl shadow-2xl p-8 border" style={{ background: 'rgba(255, 255, 255, 0.9)', borderColor: 'rgba(148, 137, 121, 0.2)' }}>
                <div className="text-center mb-8">
                  <div className="w-16 h-16 gradient-primary rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
                    <i className="fas fa-user text-white text-2xl"></i>
                  </div>
                  <h2 className="text-3xl font-bold" style={{ color: '#222831' }}>
                    Welcome Back
                  </h2>
                  <p className="mt-2" style={{ color: '#393E46' }}>Sign in to your account</p>
                </div>

                <form className="space-y-6" onSubmit={handleSubmit}>
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium mb-2" style={{ color: '#393E46' }}>
                      Email Address
                    </label>
                    <input
                      id="email"
                      name="email"
                      type="email"
                      required
                      value={formData.email}
                      onChange={handleChange}
                      className="form-input w-full"
                      placeholder="Enter your email"
                    />
                  </div>

                  <div>
                    <label htmlFor="password" className="block text-sm font-medium mb-2" style={{ color: '#393E46' }}>
                      Password
                    </label>
                    <div className="relative">
                      <input
                        id="password"
                        name="password"
                        type={showPassword ? "text" : "password"}
                        required
                        value={formData.password}
                        onChange={handleChange}
                        className="form-input w-full pr-12"
                        placeholder="Enter your password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-500 hover:text-gray-700 transition-colors"
                      >
                        <i className={`fas ${showPassword ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                      </button>
                    </div>
                  </div>

                  <div className="flex items-center justify-between">
                    <button
                      type="button"
                      onClick={() => setShowForgotPassword(true)}
                      className="text-sm font-medium hover:underline transition-all"
                      style={{ color: '#948979' }}
                    >
                      Forgot your password?
                    </button>
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full btn-primary flex items-center justify-center"
                  >
                    <i className={`fas ${loading ? 'fa-circle-notch fa-spin' : 'fa-sign-in-alt'} mr-2`}></i>
                    {loading ? 'Signing in...' : 'Sign In'}
                  </button>

                  <div className="text-center">
                    <Link to="/signup" className="font-medium hover:underline transition-all" style={{ color: '#948979' }}>
                      Don't have an account? Sign up
                    </Link>
                  </div>
                </form>
              </div>
            ) : (
              // Forgot Password Form
              <div className="backdrop-blur-sm rounded-2xl shadow-2xl p-8 border" style={{ background: 'rgba(255, 255, 255, 0.9)', borderColor: 'rgba(148, 137, 121, 0.2)' }}>
                <div className="text-center mb-8">
                  <div className="w-16 h-16 gradient-primary rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
                    <i className="fas fa-key text-white text-2xl"></i>
                  </div>
                  <h2 className="text-3xl font-bold" style={{ color: '#222831' }}>
                    {forgotPasswordStep === 1 ? 'Forgot Password' : 'Reset Password'}
                  </h2>
                  <p className="mt-2" style={{ color: '#393E46' }}>
                    {forgotPasswordStep === 1
                      ? 'Enter your email to receive an OTP'
                      : 'Enter the OTP and your new password'
                    }
                  </p>
                </div>

                <form className="space-y-6" onSubmit={handleForgotPasswordSubmit}>
                  {forgotPasswordStep === 1 ? (
                    // Step 1: Email Input
                    <div>
                      <label htmlFor="forgotEmail" className="block text-sm font-medium mb-2" style={{ color: '#393E46' }}>
                        Email Address
                      </label>
                      <input
                        id="forgotEmail"
                        name="email"
                        type="email"
                        required
                        value={forgotPasswordData.email}
                        onChange={handleForgotPasswordChange}
                        className="form-input w-full"
                        placeholder="Enter your email address"
                      />
                    </div>
                  ) : (
                    // Step 2: OTP and New Password
                    <>
                      <div>
                        <label htmlFor="otp" className="block text-sm font-medium mb-2" style={{ color: '#393E46' }}>
                          OTP Code
                        </label>
                        <input
                          id="otp"
                          name="otp"
                          type="text"
                          required
                          maxLength="6"
                          value={forgotPasswordData.otp}
                          onChange={handleForgotPasswordChange}
                          className="form-input w-full text-center text-lg tracking-widest"
                          placeholder="Enter 6-digit OTP"
                        />
                        <p className="text-xs mt-1" style={{ color: '#393E46' }}>
                          Check your email for the OTP code
                        </p>
                      </div>

                      <div>
                        <label htmlFor="newPassword" className="block text-sm font-medium mb-2" style={{ color: '#393E46' }}>
                          New Password
                        </label>
                        <div className="relative">
                          <input
                            id="newPassword"
                            name="newPassword"
                            type={showNewPassword ? "text" : "password"}
                            required
                            minLength="6"
                            value={forgotPasswordData.newPassword}
                            onChange={handleForgotPasswordChange}
                            className="form-input w-full pr-12"
                            placeholder="Enter new password"
                          />
                          <button
                            type="button"
                            onClick={() => setShowNewPassword(!showNewPassword)}
                            className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-500 hover:text-gray-700 transition-colors"
                          >
                            <i className={`fas ${showNewPassword ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                          </button>
                        </div>
                      </div>

                      <div>
                        <label htmlFor="confirmPassword" className="block text-sm font-medium mb-2" style={{ color: '#393E46' }}>
                          Confirm New Password
                        </label>
                        <div className="relative">
                          <input
                            id="confirmPassword"
                            name="confirmPassword"
                            type={showConfirmPassword ? "text" : "password"}
                            required
                            minLength="6"
                            value={forgotPasswordData.confirmPassword}
                            onChange={handleForgotPasswordChange}
                            className="form-input w-full pr-12"
                            placeholder="Confirm new password"
                          />
                          <button
                            type="button"
                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                            className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-500 hover:text-gray-700 transition-colors"
                          >
                            <i className={`fas ${showConfirmPassword ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                          </button>
                        </div>
                      </div>
                    </>
                  )}

                  <div className="flex space-x-3">
                    <button
                      type="button"
                      onClick={resetForgotPassword}
                      className="flex-1 bg-gray-300 text-gray-700 py-3 rounded-lg hover:bg-gray-400 transition-colors font-medium"
                    >
                      Back to Login
                    </button>
                    <button
                      type="submit"
                      disabled={forgotPasswordLoading}
                      className="flex-1 btn-primary flex items-center justify-center"
                    >
                      <i className={`fas ${forgotPasswordLoading ? 'fa-circle-notch fa-spin' : forgotPasswordStep === 1 ? 'fa-paper-plane' : 'fa-key'} mr-2`}></i>
                      {forgotPasswordLoading
                        ? (forgotPasswordStep === 1 ? 'Sending...' : 'Resetting...')
                        : (forgotPasswordStep === 1 ? 'Send OTP' : 'Reset Password')
                      }
                    </button>
                  </div>
                </form>
              </div>
            )}
          </div>
        </div>
        <Footer />
      </div>
    </>
  );
};

export default Login;